import { Divider, HoverText, Icon, PreIcon } from '@advanza/ui'
import { Note } from '@trustoo/types/entity/note'
import ReclaimActionsMenu from 'components/reclaims/ReclaimActionsMenu'
import ReclaimPercentageTable from 'components/reclaims/ReclaimPercentageTable'
import ReclaimReasonExplanation from 'components/reclaims/ReclaimReasonExplanation'
import A from 'components/ui/A'
import Balloon from 'components/ui/Balloon'
import { format, parseISO } from 'date-fns'
import { Request } from 'hooks/requestHooks'
import { getRequestStatusInfo } from 'hooks/requestsHooks'
import { Reclaim } from 'interfaces/Reclaims'
import type { ReactNode } from 'react'
import { getPendingAiHoverText, getPendingAiLabel } from 'utils/reclaimPendingAiHelpers'

const getDate = (value?: string | null) => (value ? parseISO(value) : null)

interface RenderReclaimFieldProps {
    key:
        | keyof Reclaim
        | 'requestStatus'
        | 'status'
        | 'firstThree'
        | 'leadName'
        | 'nrReclaims'
        | 'action'
    reclaim: Reclaim
    requestUrl?: string
    providerUrl?: string
    matchUrl?: string
    compact?: boolean
    notes?: Note[]
    request?: Request | null
}

export const renderReclaimField = ({
    key,
    reclaim,
    requestUrl,
    providerUrl,
    matchUrl,
    compact,
    notes,
    request,
}: RenderReclaimFieldProps): ReactNode => {
    switch (key) {
        case 'businessName':
            return (
                <A className="classic text-primary-600" to={providerUrl || '#'} external={false}>
                    {reclaim.businessName}
                </A>
            )

        case 'status':
            if (reclaim.deleted) {
                return (
                    <HoverText trigger={<span className="text-tertiary-800">Deleted</span>}>
                        <div>
                            <div>Reclaim deleted:</div>
                            <div>
                                {getDate(reclaim.deleted)
                                    ? format(getDate(reclaim.deleted)!, 'd-MMM-yyyy HH:mm')
                                    : '-'}
                            </div>
                            {reclaim.admin_comment && (
                                <>
                                    <div>Remark:</div>
                                    <div>{reclaim.admin_comment}</div>
                                </>
                            )}
                        </div>
                    </HoverText>
                )
            } else if (reclaim.accepted) {
                return (
                    <HoverText trigger={<span className="text-secondary-800">Accepted</span>}>
                        <div>
                            <div>Reclaim accepted:</div>
                            <div>
                                {getDate(reclaim.accepted)
                                    ? format(getDate(reclaim.accepted)!, 'd-MMM-yyyy HH:mm')
                                    : '-'}
                            </div>
                            <div>Uncharge date:</div>
                            <div>
                                {getDate(reclaim.unChargedDate)
                                    ? format(getDate(reclaim.unChargedDate)!, 'd-MMM-yyyy HH:mm')
                                    : '-'}
                                {reclaim.isFreeLeadReclaim && (
                                    <>
                                        <Divider s />
                                        <small>
                                            This lead was a free lead, so it was already uncharged.
                                            <br />
                                            The number of free leads has been increased to
                                            compensate.
                                        </small>
                                    </>
                                )}
                            </div>
                            {reclaim.admin_comment && (
                                <>
                                    <div>Admin comment:</div>
                                    <div>{reclaim.admin_comment}</div>
                                </>
                            )}
                        </div>
                    </HoverText>
                )
            } else if (reclaim.denied) {
                return (
                    <HoverText trigger={<span className="text-tertiary-800">Rejected</span>}>
                        <div>
                            <div>
                                {getDate(reclaim.denied)
                                    ? format(getDate(reclaim.denied)!, 'd-MMM HH:mm')
                                    : '-'}
                            </div>
                            {reclaim.admin_comment && <div>{reclaim.admin_comment}</div>}
                        </div>
                    </HoverText>
                )
            } else if (reclaim.withdrawn) {
                return (
                    <HoverText trigger={<span className="text-primary-500">Withdrawn</span>}>
                        <div>
                            <div>Reclaim withdrawn:</div>
                            <div>
                                {getDate(reclaim.withdrawn)
                                    ? format(getDate(reclaim.withdrawn)!, 'd-MMM-yyyy HH:mm')
                                    : '-'}
                            </div>
                            {reclaim.admin_comment && (
                                <>
                                    <div>Admin comment:</div>
                                    <div>{reclaim.admin_comment}</div>
                                </>
                            )}
                        </div>
                    </HoverText>
                )
            } else if (reclaim.is_pending_ai_processing) {
                return (
                    <HoverText
                        trigger={
                            <span className="text-primary-500">
                                {getPendingAiLabel(reclaim.auto_reclaim_evaluation_status)}
                            </span>
                        }>
                        {getPendingAiHoverText(reclaim.auto_reclaim_evaluation_status)}
                    </HoverText>
                )
            } else {
                return <span className="text-tertiary-400">Open</span>
            }

        case 'firstThree':
            return reclaim.is_open && reclaim.nrReclaimsTotal && reclaim.nrReclaimsTotal <= 3 ? (
                <Balloon content="Within first 3 reclaims">
                    <Icon name="whatshot" />
                </Balloon>
            ) : (
                ''
            )

        case 'prospect':
            return ['€€€€', '€€€€€'].includes(reclaim.prospect || '') ? (
                <span style={{ whiteSpace: 'nowrap' }}>{reclaim.prospect}</span>
            ) : (
                ''
            )

        case 'keyAccount':
            if (!reclaim.keyAccountDesc) return '-'
            const iconMap: Record<string, string> = {
                key_account: '🥇',
                silver_account: '🥈',
                bronze_account: '🥉',
            }
            return (
                <Balloon content={reclaim.keyAccountDesc} style={{ fontSize: 20 }}>
                    {iconMap[reclaim.keyAccount] || '❓'}
                </Balloon>
            )

        case 'created':
            return <small>{format(new Date(reclaim[key] ?? ''), 'd-MMM-yyyy HH:mm')}</small>

        case 'requestStatus':
            if (!reclaim.requestId) return '-'
            // Use the request object passed from parent component
            if (!request?.status) return '-'
            const { statusInfo } = getRequestStatusInfo(request.status)
            return <small>{statusInfo.label}</small>

        case 'leadName':
            return (
                <A className="classic text-primary-600" to={requestUrl || '#'} external={false}>
                    {reclaim.firstName}
                </A>
            )

        case 'phoneNumber':
            if (!reclaim.phoneNumber) return ''
            return (
                <span>
                    <Icon name="phone" /> {reclaim.phoneNumber}{' '}
                </span>
            )

        case 'matchDate':
            return (
                <A className="classic text-primary-600" to={matchUrl || '#'} external={false}>
                    <small>
                        {format(
                            new Date(
                                reclaim.phoneNumber ? reclaim.phoneClickDate : (reclaim[key] ?? ''),
                            ),
                            'd-MMM-yyyy',
                        )}
                    </small>
                </A>
            )

        case 'reason':
            return reclaim.reclaim_double_lead || reclaim.extra_field ? (
                <HoverText trigger={reclaim[key]}>
                    <ReclaimReasonExplanation
                        explanation={reclaim.extra_field}
                        doubleLead={reclaim.reclaim_double_lead}
                    />
                </HoverText>
            ) : (
                reclaim[key]
            )

        case 'nrReclaims':
            return (
                <HoverText trigger={reclaim.nrReclaimsLast3Months}>
                    Nr reclaims last 3 months
                </HoverText>
            )

        case 'nrReclaimsTotal':
            return <HoverText trigger={reclaim.nrReclaimsTotal}>Total nr of reclaims</HoverText>

        case 'reclaimPercentageTotal':
            return (
                <HoverText trigger={reclaim.reclaimPercentageTotal + '%'}>
                    <ReclaimPercentageTable reclaim={reclaim} />
                </HoverText>
            )

        case 'action':
            // Rows with an outstanding automated check email are visually locked (greyed +
            // pointer-events: none). Still let CS withdraw or delete the reclaim from such a row.
            return (
                <ReclaimActionsMenu
                    key={reclaim.reclaim_id}
                    reclaim={reclaim}
                    compact
                    locked={reclaim.automated_check_status === 'mailSent'}
                />
            )

        case 'notes':
            if (reclaim.notes?.length) {
                const reclaimKey = reclaim[key]
                const lastNoteId =
                    reclaimKey && reclaimKey.length > 0
                        ? reclaimKey[reclaimKey.length - 1]
                        : undefined

                if (lastNoteId) {
                    const note = notes?.[lastNoteId]?.note
                    const previewLength = compact ? 10 : 24
                    if (note && note.length > previewLength) {
                        return (
                            <HoverText trigger={note.slice(0, previewLength) + '...'}>
                                {note}
                            </HoverText>
                        )
                    }
                    return note
                }
            }
            return ''

        case 'extra_field':
            return reclaim.extra_field && <span>{reclaim.extra_field}</span>

        case 'automated_check_status':
            const automatedCheckStatus = reclaim[key]
            if (reclaim.is_pending_ai_processing) {
                return (
                    <PreIcon icon="smart_toy" overflowFix>
                        AI {reclaim.auto_reclaim_evaluation_status}
                    </PreIcon>
                )
            }
            return (
                automatedCheckStatus && (
                    <PreIcon icon="smart_toy" overflowFix>
                        {automatedCheckStatus.replace(/([A-Z])/g, ' $1').toLowerCase()}
                    </PreIcon>
                )
            )

        default: {
            const value = reclaim[key as keyof Reclaim]
            if (
                typeof value === 'string' ||
                typeof value === 'number' ||
                typeof value === 'boolean'
            ) {
                return value
            }
            return ''
        }
    }
}
